import React, { useEffect, useState } from 'react';
import { Modal, Spin, Button, Space } from 'antd';
import { EnvironmentOutlined, CopyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getShowRouteDetailsModal, showMapView, showRouteDetailsModal } from '../../store/app-ui.store';
import { setDraftOperations } from '../../store/operations.store';
import {
  getSelectedRouteDetails,
  getSelectedRouteDetailsLoading,
  getRouteConfirmationDetailsData,
  fetchRouteConfirmationDetails,
} from '../../store/trips.store';
import { saveLocationComment, completeTrip } from '../../store/trip-actions.store';
import RouteInformation from './RouteInformation';
import DeliveryCollectionSummary from './DeliveryCollectionSummary';
import RouteLocationsTable from './RouteLocationsTable';
import {
  useSortedLocations,
  useTableData,
  useDeliveryStatus,
  useRouteAdherence,
  useDeliveryCollectionSummary,
} from '../../hooks/useRouteDetails';
import { buildClonedOperations, downloadFile } from '../../Utils/RouteDetailsUtils';

const RouteDetails = (): JSX.Element => {
  const showModal = useAppSelector(getShowRouteDetailsModal);
  const confirmationFiles = useAppSelector(getRouteConfirmationDetailsData);
  const routeDetails = useAppSelector(getSelectedRouteDetails);
  const loading = useAppSelector(getSelectedRouteDetailsLoading);
  const [localComments, setLocalComments] = useState<Record<number, string>>({});
  const history = useHistory();
  const dispatch = useAppDispatch();

  useEffect(() => {
    setLocalComments({});
    if (routeDetails?.trip_id) {
      dispatch(fetchRouteConfirmationDetails({ tripID: routeDetails.trip_id }));
    }
  }, [dispatch, routeDetails?.trip_id]);

  const sortedLocations = useSortedLocations(routeDetails);
  const tableData = useTableData(sortedLocations, confirmationFiles, localComments);
  const deliveryStatus = useDeliveryStatus(routeDetails);
  const routeAdherence = useRouteAdherence(routeDetails);
  const collectionSummary = useDeliveryCollectionSummary(tableData);

  const close = () => {
    dispatch(showRouteDetailsModal(false));
  };

  const handleSaveComment = (locationId: number, newComment: string) => {
    setLocalComments((prev) => ({ ...prev, [locationId]: newComment }));
    dispatch(
      saveLocationComment({
        locationId,
        tripId: routeDetails.trip_id,
        comment: newComment,
      }),
    );
  };

  const handleCloneAndEdit = () => {
    if (!routeDetails) return;
    const { operations, minimalLocations } = buildClonedOperations(sortedLocations ?? []);
    dispatch(setDraftOperations({ operations, minimalLocations }));
    dispatch(showRouteDetailsModal(false));
    history.push('/operations/edit-operation');
  };

  const handleEndTrip = () => {
    if (!routeDetails?.trip_id) {
      return;
    }
    // let remarkValue = '';
    Modal.confirm({
      title: 'End this trip?',
      content: (
        <div>
          <p>Unvisited locations will be marked as skipped.</p>
          {/* <Input.TextArea
            placeholder="Add a delivery remark (optional)"
            rows={3}
            onChange={(e) => {
              remarkValue = e.target.value;
            }}
          /> */}
        </div>
      ),
      okText: 'End Trip',
      okType: 'danger',
      onOk: async () => {
        await dispatch(
          // completeTrip({ tripId: routeDetails.trip_id, remark: remarkValue.trim() || undefined }),
          completeTrip({ tripId: routeDetails.trip_id }),
        ).unwrap();
        dispatch(showRouteDetailsModal(false));
      },
    });
  };

  const openViewAllLocationMap = () => {
    const locations = sortedLocations ?? [];
    const routeLocations = locations.map((location: any, index: number) => ({
      lat: parseFloat(location.input_lat),
      lng: parseFloat(location.input_lon),
      address: location.input_address,
      sequence: index + 1,
      mapColor: index === 0 || index === locations.length - 1 ? '#29ab26' : '#4285F4',
    }));

    dispatch(
      showMapView({
        show: true,
        locations: routeLocations,
        showSequenceNumbers: true,
      }),
    );
  };

  return (
    <Modal
      visible={showModal}
      onOk={close}
      destroyOnClose
      onCancel={close}
      width={1400}
      footer={null}
      bodyStyle={{ maxHeight: '95vh', overflowY: 'auto' }}
    >
      <Spin spinning={loading}>
        <RouteInformation routeDetails={routeDetails} deliveryStatus={deliveryStatus} routeAdherence={routeAdherence} />

        <DeliveryCollectionSummary summary={collectionSummary} />

        <RouteLocationsTable tableData={tableData} onDownload={downloadFile} onSaveComment={handleSaveComment} />

        <Space className="mg-t-20 w-100 d-flex jc-center gap-16" size={16}>
          <Button type="default" icon={<EnvironmentOutlined />} onClick={openViewAllLocationMap}>
            View All Locations on Map
          </Button>
          {routeDetails?.trip_status === 'in_progress' && (
            <Button danger icon={<CheckCircleOutlined />} onClick={handleEndTrip}>
              End Trip
            </Button>
          )}
          {routeDetails?.trip_status === 'completed' && (
            <Button type="primary" icon={<CopyOutlined />} onClick={handleCloneAndEdit}>
              Clone & Edit Route
            </Button>
          )}
        </Space>
      </Spin>
    </Modal>
  );
};

export default RouteDetails;
